// Voice device bootstrap — ONE owner for init + window-event wiring.
//
// The incoming-call toast lives in Layout and listens for voice-* window
// events, so the device must exist app-wide — not behind an "Enable Calling"
// button scattered across comms pages. `bootVoiceSilently` runs once on app
// load: token → register → Device init, every failure swallowed to a debug
// log (tenants without browser calling just don't get a device). It skips
// the microphone probe — the SDK asks for the mic when a call is actually
// answered or placed. The Dialer's explicit enable path passes
// { probeMic: true } to surface permission problems up front.
import {
  fetchVoiceClientToken,
  registerVoiceDevice,
} from "../api/communications";
import {
  getVoiceDevice,
  initVoiceDevice,
  setActiveConnection,
  setLastIncoming,
} from "./voiceDevice";

let bootAttempted = false;
let lastTenantId = null;
let reconnecting = false;

const emit = (name, detail) => {
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {
    /* ignore */
  }
};

// Token refresh / silent re-register. A dropped registration or an expiring
// token must NEVER surface as "voice disconnected" or make the user reload —
// fetch a fresh token from the voice service and re-register in place, showing
// only an inline "Reconnecting…". Three consecutive failures raise one banner
// with Retry (which calls reconnectVoice again).
// Recording source (C/gulftech-f4): the browser dials Twilio directly and never
// sees the per-call decision, so the honest indicator is the tenant default the
// register route returns, (explicit toggle) ?? recording_default, and
// recording_reason says WHY it's off. Captured on EVERY register — device
// creation AND each token refresh — so flipping recording.mode mid-session
// updates the indicator without a reload (no staleness window). Dormant until
// crm-mk2 35bbd043 ships these fields; truthful the moment it does.
function applyRecordingState(registration) {
  try {
    const rec = Boolean(registration?.recording_default);
    window.__voiceRecordingDefault = rec;
    window.__voiceRecordingReason = registration?.recording_reason || null;
    emit("voice-recording", { recording: rec, reason: registration?.recording_reason || null });
  } catch {
    /* best-effort */
  }
}

async function freshToken() {
  const clientToken = await fetchVoiceClientToken(lastTenantId);
  const registration = await registerVoiceDevice(clientToken, lastTenantId);
  applyRecordingState(registration); // re-read on every refresh, not just creation
  return (
    registration?.webrtcToken ||
    registration?.client_token ||
    registration?.access_token ||
    clientToken
  );
}

async function refreshInPlace(device) {
  const token = await freshToken();
  if (typeof device.updateToken === "function") device.updateToken(token);
  if (device.state !== "registered" && typeof device.register === "function") {
    await device.register();
  }
}

export async function reconnectVoice() {
  const device = getVoiceDevice();
  if (!device || reconnecting || !lastTenantId) return false;
  reconnecting = true;
  emit("voice-reconnecting", {});
  for (let attempt = 1; attempt <= 3; attempt += 1) {
    try {
      await refreshInPlace(device);
      reconnecting = false;
      emit("voice-reconnected", {});
      return true;
    } catch {
      await new Promise((r) => setTimeout(r, 800 * attempt));
    }
  }
  reconnecting = false;
  emit("voice-reconnect-failed", {});
  return false;
}

// Device errors that mean "re-auth / re-register", not a real voice fault.
const RECOVERABLE = new Set([20101, 20103, 20104, 20151, 31005, 31009, 31204, 31205]);
const looksRecoverable = (error) =>
  RECOVERABLE.has(error?.code) ||
  /token|expir|register|jwt|reconnect/i.test(String(error?.message || ""));

export function attachDeviceEvents(device) {
  if (!device || device.__voiceListenersAttached) return;
  device.__voiceListenersAttached = true;

  device.on("incoming", (call) => {
    setLastIncoming(call);
    emit("voice-incoming", { call });
  });
  device.on("connect", (connection) => {
    setActiveConnection(connection);
    setLastIncoming(null);
    emit("voice-connect", { connection });
  });
  device.on("disconnect", () => {
    setActiveConnection(null);
    setLastIncoming(null);
    emit("voice-disconnect", {});
  });
  device.on("registered", () => emit("voice-registered", {}));

  // Refresh BEFORE the token dies — the SDK fires this ~3 min out.
  device.on("tokenWillExpire", async () => {
    try {
      await refreshInPlace(device);
    } catch {
      reconnectVoice();
    }
  });
  // A dropped registration self-heals silently.
  device.on("unregistered", () => reconnectVoice());
  device.on("registrationFailed", () => reconnectVoice());
  // Only surface a genuine, non-recoverable device error.
  device.on("error", (error) => {
    if (looksRecoverable(error)) reconnectVoice();
    else emit("voice-error", { error });
  });
}

export async function ensureVoiceReady(tenantId, { probeMic = false } = {}) {
  if (tenantId) lastTenantId = tenantId;
  const existing = getVoiceDevice();
  if (existing) {
    attachDeviceEvents(existing);
    return existing;
  }
  if (!tenantId) throw new Error("Missing tenant id for voice init");

  const clientToken = await fetchVoiceClientToken(tenantId);
  const registration = await registerVoiceDevice(clientToken, tenantId);

  applyRecordingState(registration);

  if (probeMic) {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((t) => t.stop());
  }

  const device = await initVoiceDevice(
    registration?.webrtcToken ||
      registration?.client_token ||
      registration?.access_token ||
      clientToken,
    { iceServers: registration?.iceServers }
  );
  attachDeviceEvents(device);
  return device;
}

// REMEMBER A "NO" FOR THE REST OF THE SESSION.
//
// `bootAttempted` is a module variable, so it only survives until the next full
// page load — and a full page load is exactly what a link click, a refresh or a
// deep link produces. A read-only screen walk on 2026-10-18 counted ~220 of
// these in one login pass across every role, including techs and reps who never
// take a call: each one a POST, two queries and a signed JWT the browser throws
// away, and a `voice.webrtc.token.issued` line drowning the log signal for
// registrations that are real.
//
// The cache deliberately keys on the TENANT and remembers only a definite no.
// It does NOT gate on role: `LoginView` currently returns "Member" for every
// staff login (QA-3, lane S), so a role check here would silently take browser
// calling away from the people who should have it — and a capability gate that
// subtracts a working feature is worse than the waste it saves. A 403 from the
// CRM is the server saying "not for this tenant/user", which is a real answer;
// a network blip or a 5xx is not, and must never poison the session.
const DENY_PREFIX = "voice.boot.denied.";

const denyKey = (tenantId) => `${DENY_PREFIX}${tenantId}`;

function bootDenied(tenantId) {
  try {
    return window.sessionStorage.getItem(denyKey(tenantId)) === "1";
  } catch {
    return false; // private mode / storage disabled: fall through and ask
  }
}

function rememberDenied(tenantId) {
  try {
    window.sessionStorage.setItem(denyKey(tenantId), "1");
  } catch {
    /* storage unavailable — we just ask again next load, as before */
  }
}

/** Only a deliberate server "no" is cacheable. */
function isDefiniteNo(err) {
  return err?.response?.status === 403;
}

export async function bootVoiceSilently(tenantId) {
  if (bootAttempted) return;
  bootAttempted = true;
  if (tenantId && bootDenied(tenantId)) {
    console.debug("[VOICE] silent init skipped: this tenant said no earlier this session");
    return;
  }
  try {
    await ensureVoiceReady(tenantId);
    console.debug("[VOICE] silent init ready");
  } catch (err) {
    // Expected for tenants without browser calling — never a user-facing error.
    if (tenantId && isDefiniteNo(err)) rememberDenied(tenantId);
    console.debug("[VOICE] silent init skipped:", err?.message || err);
  }
}

/** Testing seam + the escape hatch for "I just turned voice on for this tenant". */
export function resetVoiceBootState(tenantId) {
  bootAttempted = false;
  try {
    if (tenantId) window.sessionStorage.removeItem(denyKey(tenantId));
  } catch {
    /* nothing to clear */
  }
}
